import { SpreedlyFieldError } from '../../../services/helpers/SpreedlyIframe';

/**
 * i18n keys under `Components:Payment:Spreedly:SpreedlyCardForm.errors.*`. Spreedly's `errors`
 * payload carries English messages meant for developers ("Number is invalid"); the customer
 * sees these instead, and the raw messages survive only in the rejected Error for the log.
 *
 * `number_incomplete` / `cvv_incomplete` never come from Spreedly — the form raises them itself
 * on blur, from the field-event state, before tokenization is attempted.
 */
export type SpreedlyErrorKey =
  | 'number_blank'
  | 'number_invalid'
  | 'number_incomplete'
  | 'cvv_blank'
  | 'cvv_invalid'
  | 'cvv_incomplete'
  | 'expiry_invalid'
  | 'expired'
  | 'name_blank'
  | 'configuration'
  | 'generic';

/** Where an error is shown: under one of the fields, or in the form-level list when `null`. */
export type SpreedlyErrorField = 'name' | 'number' | 'expiry' | 'cvv';

const FIELD_BY_ERROR_KEY: Partial<Record<SpreedlyErrorKey, SpreedlyErrorField>> = {
  number_blank: 'number',
  number_invalid: 'number',
  number_incomplete: 'number',
  cvv_blank: 'cvv',
  cvv_invalid: 'cvv',
  cvv_incomplete: 'cvv',
  expiry_invalid: 'expiry',
  expired: 'expiry',
  name_blank: 'name',
};

export function fieldForErrorKey(key: SpreedlyErrorKey): SpreedlyErrorField | null {
  return FIELD_BY_ERROR_KEY[key] ?? null;
}

// Environment/referrer problems: nothing the customer typed, so one "we couldn't check your card"
// message — the specifics go to Sentry via the raw message.
const CONFIGURATION_KEYS = new Set([
  'errors.account_inactive',
  'errors.environment_key_parameter_required',
  'errors.invalid_environment_key_parameter',
  'errors.unknown_referrer',
  'errors.invalid_referrer',
  'errors.configuration',
  'errors.invalid_enhanced_authentication_params',
]);

const BLANK_KEYS = new Set(['errors.blank']);

export function mapSpreedlyError(error: Partial<SpreedlyFieldError> | null | undefined): SpreedlyErrorKey {
  const attribute = error?.attribute ?? '';
  const key = error?.key ?? '';

  if (CONFIGURATION_KEYS.has(key)) {
    return 'configuration';
  }

  if (key === 'errors.expired') {
    return 'expired';
  }

  switch (attribute) {
    case 'number':
      // `errors.blank_card_type` means the brand could not be derived from the digits — to the
      // customer that is an invalid number, not an empty one.
      return BLANK_KEYS.has(key) ? 'number_blank' : 'number_invalid';
    case 'cvv':
    case 'verification_value':
      return BLANK_KEYS.has(key) ? 'cvv_blank' : 'cvv_invalid';
    case 'month':
    case 'year':
      // One MM / YY field on our side, so month and year problems read as one expiry problem.
      return 'expiry_invalid';
    case 'full_name':
    case 'first_name':
    case 'last_name':
      return 'name_blank';
    default:
      return 'generic';
  }
}

/** Distinct keys in payload order — two problems on one field read once. */
export function mapSpreedlyErrors(errors: Array<Partial<SpreedlyFieldError>> | null | undefined): SpreedlyErrorKey[] {
  const keys = (errors ?? []).map(mapSpreedlyError);

  return keys.filter((key, index) => keys.indexOf(key) === index);
}

/** The developer-facing text, kept for the rejected Error / payment log only. */
export function describeSpreedlyErrors(errors: Array<Partial<SpreedlyFieldError>> | null | undefined): string {
  return (errors ?? [])
    .map((error) => error?.message ?? error?.key ?? 'unknown')
    .filter(Boolean)
    .join(', ');
}
